import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ShoppingCart,
  Search,
  Package,
  ChevronRight,
  Scale,
  FlaskConical,
  Hourglass,
  Brain,
  ShieldPlus,
  Zap,
  Bone,
  User,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getPrograms,
  type Program,
} from '@/services/storefrontService';
import { useCart } from '@/contexts/CartContext';
import { useClinic } from '@/contexts/ClinicContext';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getProgramCategory,
  sortProgramsByCategory,
  DEFAULT_PROGRAM_CONFIG,
  type ProgramCategoryConfig,
} from '@/config/programCategories';
import { Patient } from '@/lib/types';
import PatientSelection from '@/components/clinic/PrescriptionFormSheet/PatientSelection';

/** Map icon name strings to Lucide components */
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Scale,
  FlaskConical,
  Hourglass,
  Brain,
  ShieldPlus,
  Zap,
  Bone,
  Package,
};

function ProgramIcon({
  config,
  size = 'md',
}: {
  config: ProgramCategoryConfig;
  size?: 'sm' | 'md';
}) {
  const IconComponent = ICON_MAP[config.icon] || Package;
  const sizeClasses = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  return (
    <div
      className={`${sizeClasses} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}
      style={{ backgroundColor: config.color }}
    >
      <IconComponent className={`${iconSize} text-white`} />
    </div>
  );
}

const Storefront: React.FC = () => {
  const navigate = useNavigate();
  const { cartCount, selectedPatient, setSelectedPatient, setSelectedPractitioner } = useCart();
  const { clinic, isClinicStaff, isAdmin, isPractitioner } = useClinic();
  const [searchTerm, setSearchTerm] = useState('');

  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  // Only fetch programs when patient is selected
  const hasPatient = !!selectedPatient?.id;

  useEffect(() => {
    if (!clinic?.id || !hasPatient) {
      setPrograms([]);
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const doFetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPrograms({
          page: 1,
          limit: 20,
          sortBy: 'title',
          sortOrder: 'ASC',
          search: searchTerm,
          clinicId: clinic.id,
          displayMode: 'medication_select',
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          // Apply category-based sorting instead of API default
          setPrograms(sortProgramsByCategory(response.programs));
        }
      } catch (err: unknown) {
        const isCanceled =
          (err instanceof Error && err.name === 'AbortError') ||
          (err && typeof err === 'object' && ((err as { code?: string; message?: string }).code === 'ERR_CANCELED' || (err as { message?: string }).message === 'canceled'));
        if (isCanceled) return;
        console.error('Error fetching programs:', err);
        if (!controller.signal.aborted) {
          setError('Unable to load programs. Please try again.');
          setPrograms([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    doFetch();
    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [searchTerm, clinic?.id, retryKey, hasPatient]);

  const handleRetry = () => setRetryKey(k => k + 1);

  const handleNavigation = (url: string) => {
    if (isPractitioner) {
      navigate(`/practitioner-portal${url}`);
    } else {
      // Admin + clinic_staff both live at root.
      navigate(url);
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
  };

  const handleChangePatient = () => {
    setSelectedPatient(null);
    setSelectedPractitioner(null);
    setPrograms([]);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-xl sm:text-2xl font-bold">
            Health Programs
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Select medications from our curated programs
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            size="icon"
            className="relative h-10 w-10 bg-[rgb(35,31,32)] hover:bg-[rgb(35,31,32)]/90 text-white"
            onClick={() => handleNavigation('/storefront/cart')}
          >
            <ShoppingCart className="h-4 w-4" />
            {cartCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Patient Selection */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        {!hasPatient ? (
          <PatientSelection
            selectedPatient={selectedPatient}
            onPatientSelect={handlePatientSelect}
          />
        ) : (
          <>
            {/* Selected patient summary with change button */}
            <div className="flex items-center justify-between">
              <h3 className="text-[0.8rem] font-semibold uppercase text-[#60378B]">
                Patient Information
              </h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-slate-500 hover:text-slate-700"
                onClick={handleChangePatient}
              >
                <X className="h-3 w-3 mr-1" />
                Change Patient
              </Button>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 flex-shrink-0">
                <User className="h-5 w-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">
                  {selectedPatient?.full_name || `${selectedPatient?.first_name} ${selectedPatient?.last_name}`}
                </p>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                  {selectedPatient?.email && <span className="truncate">{selectedPatient.email}</span>}
                  {selectedPatient?.phone && <span>{selectedPatient.phone}</span>}
                </div>
              </div>
            </div>

          </>
        )}
      </div>

      {/* Gate: require patient selection before showing programs */}
      {!hasPatient ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-12 text-center">
          <User className="mx-auto h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">Select a patient to continue</p>
          <p className="text-xs text-slate-400 mt-1">
            Patient selection is required to browse programs and add medications.
          </p>
        </div>
      ) : (
      <>
      {/* Search Row */}
      <div className="flex flex-row items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search programs by title, subtitle, inclusions and target..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
            className="pl-10 bg-white border-slate-200 rounded-lg"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="h-1 bg-slate-200" />
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <Skeleton className="h-6 w-24 rounded-full" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="text-center py-20">
          <p className="text-slate-500 mb-4">{error}</p>
          <Button onClick={handleRetry} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      )}

      {/* Programs Grid */}
      {!loading && !error && programs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {programs.map((program, index) => {
            const category = getProgramCategory(program.title);
            const config = category || DEFAULT_PROGRAM_CONFIG;

            // Use category display name if available, otherwise API title
            const displayTitle = config.displayName || program.title;
            const displaySubtitle = config.subtitle || program.subtitle;
            const brandLabel = config.brandName;

            // Preserve existing medication/option count logic
            const optionCount = Array.isArray(program?.program_options) ? program.program_options.length : 0;
            const medicationCount =
              typeof program?.medication_count === 'number'
                ? program.medication_count
                : Array.isArray(program?.medications) && program.medications.length > 0
                  ? program.medications.length
                  : Array.isArray(program?.program_templates)
                    ? program.program_templates.length
                    : 0;

            return (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03, duration: 0.3 }}
              >
                <Card
                  className="group h-full bg-white border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer transition-all duration-300 overflow-hidden relative"
                  onClick={() => handleNavigation(`/storefront/program/${program.id}`)}
                >
                  {/* Top accent bar — category color */}
                  <div
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ backgroundColor: config.color }}
                  />

                  {/* Background gradient */}
                  <div
                    className="absolute inset-0 opacity-50 pointer-events-none"
                    style={{
                      background: `linear-gradient(to bottom right, ${config.colorLight}, transparent)`,
                    }}
                  />

                  <CardHeader className="pb-3 relative">
                    <div className="flex items-start gap-4">
                      {/* Branded program icon — consistent across all cards */}
                      <ProgramIcon config={config} />

                      <div className="flex-1 min-w-0 pt-1">
                        <CardTitle className="text-base font-semibold text-slate-900 group-hover:text-slate-800 transition-colors line-clamp-1">
                          {displayTitle}
                        </CardTitle>
                        {brandLabel && (
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            Powered by {brandLabel}
                          </p>
                        )}
                        <CardDescription className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {displaySubtitle}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 relative">
                    {program.description && (
                      <p className="text-sm text-slate-600 line-clamp-2 mb-5 leading-relaxed">
                        {program.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      {/* Category badge */}
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: config.colorLight,
                          color: config.colorText,
                        }}
                      >
                        {config.category}
                        {program?.display_mode === 'protocol_showcase'
                          ? ` · ${optionCount} options`
                          : medicationCount > 0
                            ? ` · ${medicationCount} medications`
                            : ''
                        }
                      </span>

                      <span
                        className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
                        style={{ color: config.colorText }}
                      >
                        {program?.display_mode === 'protocol_showcase' ? 'View Options' : 'View'}
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && programs.length === 0 && (
        <div className="text-center py-20">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-sm">
            {searchTerm ? 'No programs found matching your search' : 'No programs available'}
          </p>
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 text-primary"
              onClick={() => setSearchTerm('')}
            >
              Clear search
            </Button>
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default Storefront;
